import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircle, Error, NetworkCheck } from "@mui/icons-material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { hostname } from "../../../utils";

interface AWSIntegrationFormProps {
  editMode?: boolean;
  initialData?: {
    region?: string;
    accessKeyId?: string;
    secretAccessKey?: string;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: {
    region: string;
    accessKeyId: string;
    secretAccessKey: string;
  }) => void;
  getToken: () => Promise<string>;
}

export default function AWSIntegrationForm({
  editMode = false,
  initialData,
  onValidationChange,
  onDataChange,
  getToken,
}: AWSIntegrationFormProps) {
  const [region, setRegion] = useState(initialData?.region || "us-east-1");
  const [accessKeyId, setAccessKeyId] = useState(
    initialData?.accessKeyId || ""
  );
  const [secretAccessKey, setSecretAccessKey] = useState(
    initialData?.secretAccessKey || ""
  );

  // Credential test state
  const [isTestingCredentials, setIsTestingCredentials] = useState(false);
  const [credentialTestResult, setCredentialTestResult] = useState<{
    success: boolean;
    results?: {
      max24HourSend: number;
      maxSendRate: number;
      sentLast24Hours: number;
      verifiedEmails: string[];
    };
    error?: string;
  } | null>(null);

  // Common AWS regions for SES
  const commonRegions = [
    { value: "us-east-1", label: "US East (N. Virginia)" },
    { value: "us-east-2", label: "US East (Ohio)" },
    { value: "us-west-1", label: "US West (N. California)" },
    { value: "us-west-2", label: "US West (Oregon)" },
    { value: "eu-west-1", label: "Europe (Ireland)" },
    { value: "eu-central-1", label: "Europe (Frankfurt)" },
    { value: "ap-southeast-1", label: "Asia Pacific (Singapore)" },
    { value: "ap-southeast-2", label: "Asia Pacific (Sydney)" },
    { value: "ap-northeast-1", label: "Asia Pacific (Tokyo)" },
  ];

  // Validate form
  useEffect(() => {
    const isValid =
      region.trim() !== "" &&
      accessKeyId.trim() !== "" &&
      secretAccessKey.trim() !== "";
    onValidationChange(isValid);
  }, [region, accessKeyId, secretAccessKey, onValidationChange]);

  // Notify parent of data changes
  useEffect(() => {
    onDataChange({
      region,
      accessKeyId,
      secretAccessKey,
    });
  }, [region, accessKeyId, secretAccessKey, onDataChange]);

  // Clear test results when credentials change
  const handleFieldChange = (
    field: "region" | "accessKeyId" | "secretAccessKey",
    value: string
  ) => {
    setCredentialTestResult(null);
    if (field === "region") setRegion(value);
    else if (field === "accessKeyId") setAccessKeyId(value);
    else if (field === "secretAccessKey") setSecretAccessKey(value);
  };

  const testCredentials = async () => {
    // Validate required fields
    if (!region.trim() || !accessKeyId.trim() || !secretAccessKey.trim()) {
      setCredentialTestResult({
        success: false,
        error:
          "Please fill in all required fields (Region, Access Key ID, and Secret Access Key) before testing credentials.",
      });
      return;
    }

    setIsTestingCredentials(true);
    setCredentialTestResult(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/integrations/aws/test-credentials`,
        {
          region,
          accessKeyId,
          secretAccessKey,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setCredentialTestResult({
        success: response.data.success,
        results: response.data.results,
      });
    } catch (error: any) {
      setCredentialTestResult({
        success: false,
        error:
          error.response?.data?.error ||
          "Credential test failed. Please check your settings and try again.",
      });
    } finally {
      setIsTestingCredentials(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" gutterBottom>
        AWS SES Configuration
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Configure your AWS SES (Simple Email Service) credentials to send
        emails. Make sure your IAM user has the necessary SES permissions and
        your sending email addresses are verified in SES.
      </Alert>

      {/* Quick Setup for Common Regions */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Quick Select Region:
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {commonRegions.slice(0, 5).map((r) => (
            <Box
              key={r.value}
              onClick={() => handleFieldChange("region", r.value)}
              sx={{
                p: 1,
                border: 1,
                borderColor: region === r.value ? "primary.main" : "divider",
                borderRadius: 1,
                cursor: "pointer",
                bgcolor: region === r.value ? "action.selected" : "transparent",
                "&:hover": {
                  bgcolor: "action.hover",
                },
              }}
            >
              <Typography variant="caption">{r.label}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <TextField
        sx={{ mt: 2 }}
        fullWidth
        value={region}
        label="AWS Region"
        placeholder="us-east-1"
        onChange={(e) => handleFieldChange("region", e.target.value)}
        helperText="The AWS region where your SES service is configured"
        required
      />
      <TextField
        sx={{ mt: 2 }}
        fullWidth
        value={accessKeyId}
        label="AWS Access Key ID"
        placeholder="AKIAIOSFODNN7EXAMPLE"
        onChange={(e) => handleFieldChange("accessKeyId", e.target.value)}
        helperText="Your AWS IAM user's access key ID"
        required
      />
      <TextField
        sx={{ mt: 2 }}
        fullWidth
        value={secretAccessKey}
        label="AWS Secret Access Key"
        type="password"
        placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
        onChange={(e) => handleFieldChange("secretAccessKey", e.target.value)}
        helperText="Your AWS IAM user's secret access key"
        required
      />

      <Divider sx={{ my: 3 }} />

      {/* Credential Test Section */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={
              isTestingCredentials ? (
                <CircularProgress size={20} />
              ) : (
                <NetworkCheck />
              )
            }
            onClick={testCredentials}
            disabled={isTestingCredentials}
            size="small"
          >
            {isTestingCredentials ? "Testing..." : "Check Credentials"}
          </Button>

          {credentialTestResult && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {credentialTestResult.success ? (
                <Chip
                  icon={<CheckCircle />}
                  label="Credentials Valid"
                  color="primary"
                  size="small"
                />
              ) : (
                <Chip
                  icon={<Error />}
                  label="Credentials Invalid"
                  color="error"
                  size="small"
                />
              )}
            </Box>
          )}
        </Box>

        {credentialTestResult && (
          <Box sx={{ mt: 1 }}>
            {credentialTestResult.success && credentialTestResult.results ? (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  AWS SES credentials are valid and working!
                </Alert>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  <Chip
                    label={`Daily Limit: ${credentialTestResult.results.max24HourSend.toLocaleString()}`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Send Rate: ${credentialTestResult.results.maxSendRate}/sec`}
                    size="small"
                    variant="outlined"
                  />
                  <Chip
                    label={`Sent Today: ${credentialTestResult.results.sentLast24Hours.toLocaleString()}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
                {credentialTestResult.results.verifiedEmails.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Verified email addresses:
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 0.5 }}>
                      {credentialTestResult.results.verifiedEmails.map(
                        (email) => (
                          <Chip
                            key={email}
                            label={email}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        )
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="error" sx={{ mt: 1 }}>
                {credentialTestResult.error || "Credential test failed"}
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
